import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Icon } from './Icon';
import { Z } from '../constants/tokens';

/**
 * The app's replacement for window.confirm().
 *
 * Rendered once at app level by ConfirmProvider; call sites never mount this
 * directly, they await useConfirm()'s promise. Follows the project's modal
 * conventions (centred overlay, dark backdrop, destructive action in red
 * #ef4444) and the same accessibility contract as the other modals: Escape
 * cancels, focus moves in on open and returns to the opener on close, Tab is
 * trapped, and the dialog is announced via role/aria-modal/aria-labelledby.
 *
 * Cancel is focused on open rather than Confirm, so a stray Enter cannot
 * destroy anything — these guard deletions of testimony data.
 */
export default function ConfirmDialog({
  message,
  title = 'Are you sure?',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = true,
  theme = {},
  onResolve,
}) {
  const dialogRef = React.useRef(null);
  const cancelRef = React.useRef(null);

  React.useEffect(() => {
    const opener = document.activeElement;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onResolve(false); return; }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const f = dialogRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown, true);
    cancelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, [onResolve]);

  const accent = destructive ? '#ef4444' : (theme.glossaryAccent || '#3b82f6');

  return (
    <div
      onClick={() => onResolve(false)}
      style={{
        position: 'fixed', inset: 0, zIndex: Z.dialog,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px', backgroundColor: 'rgba(0,0,0,0.55)',
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="gwv-confirm-title"
        aria-describedby="gwv-confirm-message"
        tabIndex={-1}
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '440px', outline: 'none',
          background: theme.sidebar || theme.card || '#fff',
          border: `1px solid ${theme.border || '#e5e7eb'}`,
          borderRadius: '14px', boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', gap: '12px', padding: '18px 20px 14px' }}>
          <div style={{ flexShrink: 0, marginTop: '1px' }}>
            <Icon icon={AlertTriangle} size={20} color={accent} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div id="gwv-confirm-title" style={{ fontWeight: 700, fontSize: '0.95rem', color: theme.text || '#111', marginBottom: '5px' }}>
              {title}
            </div>
            <div id="gwv-confirm-message" dir="auto" style={{ fontSize: '0.82rem', color: theme.textSub || '#555', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
              {message}
            </div>
          </div>
        </div>

        {/* Actions sit below a divider, matching the app's modal editors */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: '8px',
          padding: '12px 20px', borderTop: `1px solid ${theme.border || '#e5e7eb'}`,
        }}>
          <button
            ref={cancelRef}
            type="button"
            onClick={() => onResolve(false)}
            style={{
              padding: '8px 16px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600,
              background: 'transparent', color: theme.textSub || '#555',
              border: `1px solid ${theme.border || '#e5e7eb'}`, cursor: 'pointer',
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => onResolve(true)}
            style={{
              padding: '8px 16px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 700,
              background: accent, color: '#fff', border: 'none', cursor: 'pointer',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
